import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { simpleCopy } from '@/utils/deepCopy';
import { InputExcelInfosRepository } from '@/coreInfrastructureModule/Repositories/InputExcelInfosRepository';
import { AlterableHeaderInfo, SelectYearMonthPatternInHeader, SelectYearMonthPatternInlined } from '../pages/interfaces/LinkingInput';
import { InputNumberInput } from '../pages/interfaces/InputField';
import { SelectYearMonthPattern, UploadInputFileType } from '../pages/interfaces/UploadFile';
import { PutExcelInputInfoAdapter } from '../adapters/PutExcelInputInfoAdapter';
import { assertNever } from '@/coreDomainsModule/assertNever';
import { RegisterOrderExcelInfos } from '@/coreDomainsModule/entities/InputOrderExcelInfo';

/**
 * 発注以外の入力ファイルタイプ
 */
type StandardUploadInputFileType = Exclude<UploadInputFileType, '発注'>;

/**
 * UI上の入力ファイルタイプを保存用のファイルタイプに変換する
 * @param uploadInputFileType - UI上の入力ファイルタイプ
 */
const toStandardInputFileType = (uploadInputFileType: StandardUploadInputFileType): 'Inventory' | 'SalesResults' | 'Arrival' => {
  switch (uploadInputFileType) {
    case '在庫':
      return 'Inventory';
    case '販売実績':
      return 'SalesResults';
    case '入荷':
      return 'Arrival';
    default:
      assertNever(uploadInputFileType);
      return 'Inventory';
  }
};

/**
 * 入力エクセルの読み込み情報を登録する
 */
export class RegisterExcelInputInfoUseCase {
  /**
   *  通常入力ファイルのデータを保存する
   * @param targetKeyName - 変更するキー名
   * @param registerKeyName - 登録するキー名
   * @param uploadInputFileType - ファイルタイプ
   * @param headerNumber - ヘッダー行番号
   * @param useFormatType - 年月情報の取得パターン
   * @param alternateHeaderInfos - 読み替え可能なヘッダー情報
   * @param inlinedYearMonthInfo - 年月情報の取得パターン（インライン）
   * @param inHeaderYearMonthInfo - 年月情報の取得パターン（ヘッダー）
   * @param adapter - アダプタ
   * @param repository - リポジトリ
   */
  public async ExecuteStandard(
    targetKeyName: string,
    registerKeyName: string,
    uploadInputFileType: StandardUploadInputFileType,
    headerNumber: InputNumberInput,
    useFormatType: SelectYearMonthPattern,
    alternateHeaderInfos: AlterableHeaderInfo[],
    inlinedYearMonthInfo: SelectYearMonthPatternInlined,
    inHeaderYearMonthInfo: SelectYearMonthPatternInHeader,
    adapter: PutExcelInputInfoAdapter,
    repository: InputExcelInfosRepository
  ): Promise<INetworkResult<undefined, IError>> {
    try {
      const registerData = simpleCopy(
        adapter.adaptStandard(headerNumber, useFormatType, alternateHeaderInfos, inlinedYearMonthInfo, inHeaderYearMonthInfo)
      );
      const inputFileType = toStandardInputFileType(uploadInputFileType);
      const response = await repository.put(targetKeyName, registerKeyName, inputFileType, registerData);
      if (response.status.type === 'success') {
        return {
          status: {
            type: 'success',
            data: undefined,
          },
        };
      } else {
        return {
          // 基本的にはここには来ない
          status: {
            type: 'error',
            error: response.status.error,
          },
        };
      }
    } catch (error) {
      console.error('error', error); // 基本的にはここには来ない
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 500,
            code: 'unknown',
            message: '未知のエラーが発生しました',
          },
        },
      };
    }
  }

  /**
   * 発注入力ファイルのデータを保存する
   * @param targetKeyName - 変更するキー名
   * @param registerKeyName - 登録するキー名
   * @param orderExcelInfos - 発注エクセル登録設定一覧
   * @param adapter - アダプタ
   * @param repository - リポジトリ
   */
  public async ExecuteOrder(
    targetKeyName: string,
    registerKeyName: string,
    orderExcelInfos: RegisterOrderExcelInfos,
    adapter: PutExcelInputInfoAdapter,
    repository: InputExcelInfosRepository
  ): Promise<INetworkResult<undefined, IError>> {
    try {
      const registerData = simpleCopy(adapter.adaptOrder(registerKeyName, orderExcelInfos));
      const response = await repository.put(targetKeyName, registerKeyName, 'Order', registerData);
      if (response.status.type === 'success') {
        return {
          status: {
            type: 'success',
            data: undefined,
          },
        };
      } else {
        return {
          // 基本的にはここには来ない
          status: {
            type: 'error',
            error: response.status.error,
          },
        };
      }
    } catch (error) {
      console.error('error', error); // 基本的にはここには来ない
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 500,
            code: 'unknown',
            message: '未知のエラーが発生しました',
          },
        },
      };
    }
  }
}
